/**
 * useLeadImport Hook
 *
 * Gerencia o estado de importação de leads com feedback em tempo real via Socket.IO
 * Padrão de referência: useCalendarSync
 */

import { useState, useEffect, useCallback } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import { toast } from 'sonner';

interface LeadImportState {
  isImporting: boolean;
  currentItem: number;
  totalItems: number;
  message: string;
  stage: 'extracting' | 'chunking' | 'processing' | 'validating' | 'inserting' | 'completed' | '';
  percentage: number;
  errors: number;
}

interface LeadImportProgressEvent {
  stepId: string;
  stage: 'extracting' | 'chunking' | 'processing' | 'validating' | 'inserting' | 'completed';
  currentChunk?: number;
  totalChunks?: number;
  processedLeads: number;
  totalLeads: number;
  errors: number;
  percentage: number;
}

interface UseLeadImportOptions {
  stepId?: string | null;
  onImportCompleted?: () => void;
}

export const useLeadImport = (options?: UseLeadImportOptions) => {
  const { stepId, onImportCompleted } = options || {};
  const { socket, isConnected } = useSocket();

  const [importState, setImportState] = useState<LeadImportState>({
    isImporting: false,
    currentItem: 0,
    totalItems: 0,
    message: '',
    stage: '',
    percentage: 0,
    errors: 0
  });

  /**
   * Traduz stage para mensagem amigável
   */
  const getStageMessage = useCallback((stage: string, currentItem: number, totalItems: number): string => {
    switch (stage) {
      case 'extracting':
        return 'Extraindo texto do arquivo...';
      case 'chunking':
        return 'Dividindo arquivo em partes...';
      case 'processing':
        // ✅ Só mostra contador quando totalItems > 0
        return totalItems > 0
          ? `Processando leads com IA... (${currentItem}/${totalItems})`
          : 'Processando leads com IA...';
      case 'validating':
        // ✅ Só mostra contador quando totalItems > 0
        return totalItems > 0
          ? `Validando leads... (${currentItem}/${totalItems})`
          : 'Validando leads...';
      case 'inserting':
        // ✅ Só mostra contador quando totalItems > 0
        return totalItems > 0
          ? `Inserindo leads... (${currentItem}/${totalItems})`
          : 'Inserindo leads...';
      case 'completed':
        return 'Importação concluída!';
      default:
        return 'Importando leads...';
    }
  }, []);

  /**
   * Handler para progresso de importação
   */
  const handleImportProgress = useCallback((data: LeadImportProgressEvent) => {
    // Filtrar eventos apenas do stepId atual
    if (stepId && data.stepId !== stepId) {
      return;
    }

    const currentItem = data.processedLeads || 0;
    const totalItems = data.totalLeads || 0;
    const message = getStageMessage(data.stage, currentItem, totalItems);

    setImportState({
      isImporting: true,
      currentItem,
      totalItems,
      message,
      stage: data.stage,
      percentage: data.percentage,
      errors: data.errors
    });

    // Atualizar toast com progresso
    if (data.stage === 'completed') {
      toast.success(`Importação concluída! ${currentItem} leads importados`, {
        id: 'lead-import',
        duration: 4000
      });

      // Resetar estado após 1 segundo
      setTimeout(() => {
        setImportState({
          isImporting: false,
          currentItem: 0,
          totalItems: 0,
          message: '',
          stage: '',
          percentage: 0,
          errors: 0
        });

        // Chamar callback de conclusão
        if (onImportCompleted) {
          onImportCompleted();
        }
      }, 1000);

    } else {
      toast.loading(message, {
        id: 'lead-import',
        duration: Infinity
      });
    }
  }, [stepId, getStageMessage, onImportCompleted]);

  /**
   * Subscribe/unsubscribe Socket.IO events
   */
  useEffect(() => {
    if (!socket || !isConnected) return;

    console.log('🔌 [useLeadImport] Subscribing to lead:import-progress');

    socket.on('lead:import-progress', handleImportProgress);

    return () => {
      console.log('🔌 [useLeadImport] Unsubscribing from lead:import-progress');
      socket.off('lead:import-progress', handleImportProgress);
    };
  }, [socket, isConnected, handleImportProgress]);

  return {
    isImporting: importState.isImporting,
    currentItem: importState.currentItem,
    totalItems: importState.totalItems,
    message: importState.message,
    stage: importState.stage,
    percentage: importState.percentage,
    errors: importState.errors,
    isSocketConnected: isConnected
  };
};
